import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { noContent, tokenIssuedResponse, tokensResponse, type Token } from "@sitebrew/contracts/v1";

/*
 * Re-exported for the fleet-copied components (`components/user-management`,
 * held byte-for-byte to the archive by `fleet-copy.test.js`), which import
 * their types from here. Nothing is declared here any more.
 */
export type { IssuedToken, Token } from "@sitebrew/contracts/v1";
import { keys } from "./query-keys";

/**
 * API tokens, as the console sees them (`sitebrew-api` design note 0003).
 *
 * The addressing is the API's: a list, a create and a revoke all carry the
 * workspace in the path, because that is where a key belongs — a key is issued
 * inside one workspace and grants access to that one only, so somebody who
 * works in two needs two keys (design note 0003, *What a token grants*).
 */

/*
 * `Token` and `IssuedToken` are `@sitebrew/contracts/v1`'s (#289 row 34):
 * §7's exact key set, `scopes` deliberately absent, `createdBy` a **user id**
 * rather than an email — see `creatorLabel`.
 */

/**
 * Is this key still usable?
 *
 * Asked here rather than in the row, because "live" is three facts — revoked,
 * expired, neither — and the screen needs the same answer in two places: the
 * badge on the row and the sentence about what the key can still do. The API
 * decides the same question its own way when a key is presented; this is what
 * the listing can see, and the two agree because both read `revokedAt` and
 * `expiresAt`.
 */
export type TokenState = "live" | "revoked" | "expired";

export function tokenState(token: Token, now: number = Date.now()): TokenState {
  if (token.revokedAt !== null) return "revoked";
  if (token.expiresAt !== null && token.expiresAt <= now) return "expired";
  return "live";
}

/**
 * Who issued a key, as a person rather than as an id.
 *
 * `createdBy` is a user id and `/v1` has no route that turns one into a name:
 * `/me` answers for the signed-in account and nothing else. So the only id this
 * console can resolve is its own, and every other key shows the id it was given.
 *
 * That is a gap and it is deliberate rather than forgotten — raised on
 * `sitebrew-api#18`, where the answer belongs, because the fix is either a field
 * in the listing or a members route. This function is the one place it lands, so
 * the day the API names the person, one body changes.
 */
export function creatorLabel(token: Token, me: { id: string; email: string } | undefined): string {
  return me && token.createdBy === me.id ? me.email : token.createdBy;
}

/**
 * How long a new key lasts, as the dialog offers it.
 *
 * The API's own rule is three cases: the field omitted means 90 days, `null`
 * means never, a number means that moment (design note 0003). The dialog sends
 * an **explicit** value for every choice including the default one, and that is
 * the deliberate half: what somebody picked from this list is what is stored, so
 * the row they read back says what they chose. Omitting the field to inherit the
 * API's default would put the number 90 on this screen as a label whose truth
 * lives in another repository — the day that default changes, the label would go
 * on saying 90 and nothing would fail.
 */
export const EXPIRY_CHOICES = [
  { id: "30", label: "30 days", days: 30 },
  { id: "60", label: "60 days", days: 60 },
  { id: "90", label: "90 days", days: 90 },
  { id: "365", label: "1 year", days: 365 },
  /** The visible half of Jakub's decision of 15 August: forever is a choice. */
  { id: "never", label: "Never", days: null },
] as const;

export const DEFAULT_EXPIRY_CHOICE = "90";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The moment a choice made *now* expires at, or `null` for a key that does not. */
export function expiryFrom(choiceId: string, now: number = Date.now()): number | null {
  const choice = EXPIRY_CHOICES.find((each) => each.id === choiceId);
  return choice?.days == null ? null : now + choice.days * DAY_MS;
}

/**
 * The keys of one workspace.
 *
 * A plain query and not a `DataTable` page: the fleet's screen is a bordered
 * table with no pager, search or sorting, and a workspace holds at most twenty
 * keys (design note 0003) — so there is nothing to page through, and reaching
 * for the table component here would be our own idea rather than the fleet's
 * screen ([MEM-131]).
 */
export function useTokens(workspaceId: string) {
  return useQuery({
    queryKey: keys.tokens(workspaceId),
    queryFn: () =>
      api(`/workspaces/${workspaceId}/tokens`, tokensResponse).then((body) => body.tokens),
  });
}

/**
 * Issue a key.
 *
 * The answer carries the value, and it is handed to the caller rather than put
 * in the cache: the listing's rows are §7's key set and the value is not in it,
 * so a cache holding one would be the credential sitting in memory under a key
 * anything can read, for as long as the tab is open.
 *
 * Refusals are not reworded here. `quota_exceeded` says a workspace may hold
 * twenty live keys and `invalid_name` says what a name is; both are the API's
 * sentences, and a second copy in the console is a second thing to keep in step
 * (the same rule `sites.ts` follows).
 */
export function useCreateToken(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string; expiresAt: number | null }) =>
      api(`/workspaces/${workspaceId}/tokens`, tokenIssuedResponse, {
        method: "POST",
        body: input,
      }).then((body) => body.token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.tokens(workspaceId) });
    },
  });
}

/**
 * Revoke a key.
 *
 * `204` and nothing to read back, and — unlike an archived site — the row does
 * **not** disappear: a revoked key stays in the listing with `revokedAt` set,
 * because a key that vanishes takes the answer to "was this the one that leaked"
 * with it (design note 0003). So the list is invalidated rather than the row
 * removed, and what comes back is the same key wearing its new state.
 */
export function useRevokeToken(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tokenId: string) =>
      api(`/workspaces/${workspaceId}/tokens/${tokenId}`, noContent, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.tokens(workspaceId) });
    },
  });
}
